import { Alert, Button, Card, message, Tooltip } from 'antd';
import React, { useState } from 'react';

import type { ProjectLink } from '@/api/project-details/mocks/projectHome.types';
import type { ResolveSharePointSharingLinkResponseDTO } from '@/api/projects/api';
import Text from '@/atoms/Text';
import { Pill } from '@/components/common/Pill';
import {
  AddProjectLinkModal,
  type AddProjectLinkPayload,
} from '@/components/project_details_components/AddProjectLinkModal';
import { CloseIcon as CloseOutlined, CopyIcon as CopyOutlined, LinkIcon } from '@/icons';
import { createLogger } from '@/utils/logging';

const logger = createLogger('ProjectLinksCard', 'component');

// Mirrors the backend's auto-generated HubSpot deal link marker (see
// _HUBSPOT_URL_MARKER in projects_controller.py) — that link can't be deleted.
const HUBSPOT_URL_MARKER = 'app.hubspot.com';

export interface ProjectLinksCardProps {
  links: ProjectLink[];
  clientId: string;
  projectId: string;
  accessToken?: string | null;
  microsoftOboAssertion?: string | null;
  sharepointFolderWebUrl?: string | null;
  onLinkAdd?: (link: { label: string; url: string }) => Promise<void>;
  onSharePointSave?: (resolved: ResolveSharePointSharingLinkResponseDTO) => Promise<void>;
  onLinkDelete?: (linkId: string) => Promise<void>;
  isLoading?: boolean;
  isDeletingId?: string | null;
  canManageLinks?: boolean;
  error?: string | null;
  onErrorDismiss?: () => void;
}

export function ProjectLinksCard({
  links,
  clientId,
  projectId,
  accessToken,
  microsoftOboAssertion,
  sharepointFolderWebUrl,
  onLinkAdd,
  onSharePointSave,
  onLinkDelete,
  isLoading = false,
  isDeletingId = null,
  canManageLinks = true,
  error = null,
  onErrorDismiss,
}: Readonly<ProjectLinksCardProps>): React.ReactElement {
  const [modalOpen, setModalOpen] = useState(false);
  const [localDeletingId, setLocalDeletingId] = useState<string | null>(null);

  const deletingId = localDeletingId ?? isDeletingId;

  const handleModalSubmit = async (data: AddProjectLinkPayload): Promise<void> => {
    if (data.type === 'sharepoint') {
      await onSharePointSave?.(data.resolved);
    } else {
      await onLinkAdd?.({ label: data.label, url: data.url });
    }
  };

  const handleCopyUrl = async (url: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      void message.success('Link copied to clipboard');
    } catch (error) {
      logger.error('Failed to copy link:', error);
      void message.error('Failed to copy link');
    }
  };

  const handleRemoveLink = async (linkId: string): Promise<void> => {
    if (!onLinkDelete || !linkId) {
      logger.error('Cannot delete link: missing link ID');
      return;
    }

    setLocalDeletingId(linkId);

    try {
      await onLinkDelete(linkId);
    } catch (error) {
      logger.error('Failed to delete project link:', error);
    } finally {
      setLocalDeletingId(null);
    }
  };

  return (
    <>
      {error && (
        <Alert
          message="Permission Denied"
          description={error}
          type="error"
          showIcon
          closable
          onClose={onErrorDismiss}
          style={{ marginBottom: '16px' }}
        />
      )}
      <Card className="project-links-card">
        <div className="project-links-card__header-top">
          <Pill tone="neutral">Source: Manual</Pill>
        </div>

        <div className="card__header">
          <Text variant="h5">Links</Text>

          <Button
            type="text"
            size="small"
            className="project-links-card__add-button"
            onClick={() => setModalOpen(true)}
            disabled={isLoading || !canManageLinks}
            loading={isLoading}
          >
            + Add
          </Button>
        </div>

        <div className="project-links-card__items">
          {sharepointFolderWebUrl ? (
            <div className="project-links-card__item project-links-card__item--sharepoint">
              <div className="project-links-card__link-content">
                <Tooltip title="Copy link">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    className="project-links-card__link-icon"
                    onClick={() => void handleCopyUrl(sharepointFolderWebUrl)}
                  />
                </Tooltip>
                <Tooltip title={sharepointFolderWebUrl}>
                  <a
                    href={sharepointFolderWebUrl}
                    className="project-links-card__link-label"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <LinkIcon style={{ marginRight: 4 }} />
                    SharePoint Folder
                  </a>
                </Tooltip>
              </div>
            </div>
          ) : null}

          {links.map((link) => {
            const isDeleting = deletingId === link.id;
            const isHubspotLink = link.url.includes(HUBSPOT_URL_MARKER);

            return (
              <div key={link.id} className="project-links-card__item">
                <div className="project-links-card__link-content">
                  <Tooltip title="Copy link">
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      className="project-links-card__link-icon"
                      onClick={() => {
                        void handleCopyUrl(link.url);
                      }}
                    />
                  </Tooltip>

                  <Tooltip title={link.url}>
                    <a
                      href={link.url}
                      className="project-links-card__link-label"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {link.label}
                    </a>
                  </Tooltip>
                </div>

                {isHubspotLink ? null : (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    className="project-links-card__remove-button"
                    onClick={() => {
                      void handleRemoveLink(link.id);
                    }}
                    disabled={isDeleting}
                    loading={isDeleting}
                  />
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <AddProjectLinkModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
        clientId={clientId}
        projectId={projectId}
        accessToken={accessToken}
        microsoftOboAssertion={microsoftOboAssertion}
      />
    </>
  );
}
